import { useEffect, useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router';

import { DashboardStep } from '@/features/Dashboard/constants/DashboardStep';
import { EditDashboardDraftProvider } from '@/features/Dashboard/context/EditDashboardDraft';
import { DashboardDrawer } from '@/features/Dashboard/drawer';
import { DrawerStepsProvider } from '@/features/Dashboard/drawer/context/DrawerStepsContext';
import { AppFormProvider } from '@/features/Dashboard/form/AppForm/context';
import { EmptyDashboard } from '@/features/Dashboard/pages/Empty';
import { ViewDashboard } from '@/features/Dashboard/pages/View';
import { MarketApps } from '@/features/Dashboard/types';
import { useForwardCatalog, useSpotCatalog } from '@/hooks/api/catalog';
import { useDashboard } from '@/hooks/api/dashboard';
import MarketsSkeleton from '@/pages/Markets/MarketsSkeleton';

import { EditDashboard } from './pages/Edit';

const Dashboard = () => {
  const [title, setTitle] = useState('New Dashboard');
  const [step, setStep] = useState(DashboardStep.Empty);
  const [dashboardConfig, setDashboardConfig] = useState<MarketApps | undefined>();
  const { data: dashboardData, isLoading } = useDashboard();
  const navigate = useNavigate();

  const params = useParams();

  useEffect(() => {
    if (!dashboardData) return;

    const param = params.dashboardId ?? '';

    if (!param || param.trim() === '') {
      const firstDashboardTitle = Object.keys(dashboardData)[0];
      if (firstDashboardTitle) {
        navigate(`/dashboard/${encodeURIComponent(firstDashboardTitle)}`, { replace: true });
        return;
      }
      return;
    }

    const unsanitizedTitle = decodeURIComponent(param);
    setTitle(unsanitizedTitle);
    const dashboard = dashboardData[unsanitizedTitle];
    if (dashboard) {
      setDashboardConfig(dashboard);
    } else {
      navigate('/notfound', { replace: true });
    }
  }, [dashboardData, navigate, params.dashboardId]);

  useForwardCatalog();
  useSpotCatalog();
  useEffect(() => {
    if (!isLoading && (!dashboardConfig || dashboardConfig.length === 0)) {
      setStep(DashboardStep.Empty);
    } else {
      setStep(DashboardStep.View);
    }
  }, [dashboardConfig, isLoading]);
  const content = useMemo(() => {
    if (isLoading) {
      return <MarketsSkeleton />;
    }
    switch (step) {
      case DashboardStep.Empty:
        return <EmptyDashboard />;
      case DashboardStep.View:
        return (
          <ViewDashboard
            dashboardConfig={dashboardConfig}
            setStep={setStep}
          />
        );
      case DashboardStep.Edit:
        return (
          <EditDashboard
            dashboardConfig={dashboardConfig!}
            onClose={() => {
              setStep(DashboardStep.View);
            }}
          />
        );
      default:
        return null;
    }
  }, [step, dashboardConfig, isLoading]);

  return (
    <EditDashboardDraftProvider initialDashboardName={title}>
      <AppFormProvider>
        <DrawerStepsProvider>
          {content}
          <DashboardDrawer />
        </DrawerStepsProvider>
      </AppFormProvider>
    </EditDashboardDraftProvider>
  );
};

export default Dashboard;
